import { useState, useCallback, useRef, useEffect } from 'react';

// Industry-standard column-width thresholds.
// Text columns default to an 80px floor so headers stay readable; icon/checkbox-only
// columns (no label, e.g. a row-action arrow) should pass minWidth: 50 explicitly.
const DEFAULT_MIN_COL_WIDTH = 80;
const ICON_COL_MIN_WIDTH = 50;
const DEFAULT_MAX_COL_WIDTH = 500;

/**
 * Reusable draggable-column-width hook for data tables.
 *
 * @param {Array<{key: string, width: number, minWidth?: number, maxWidth?: number}>} columns
 *   - width: starting/"ideal" px width for this column
 *   - minWidth: floor while dragging (default 80 for text columns; pass ICON_COL_MIN_WIDTH
 *     or 50 for icon-only columns with no header label)
 *   - maxWidth: ceiling while dragging (default 500, to stop text stretching edge-to-edge)
 * @returns {{ colWidths, tableWidth, handleResizeStart }}
 */
function useResizableColumns(columns) {
  const [colWidths, setColWidths] = useState(() =>
    columns.reduce((acc, c) => ({ ...acc, [c.key]: c.width }), {})
  );
  const limitsRef = useRef({});
  limitsRef.current = columns.reduce((acc, c) => ({
    ...acc,
    [c.key]: {
      min: c.minWidth ?? DEFAULT_MIN_COL_WIDTH,
      max: c.maxWidth ?? DEFAULT_MAX_COL_WIDTH,
    },
  }), {});
  const resizingRef = useRef(null);

  const handleResizeMove = useCallback((e) => {
    if (!resizingRef.current) return;
    const { key, startX, startWidth } = resizingRef.current;
    const { min, max } = limitsRef.current[key];
    const newWidth = Math.min(max, Math.max(min, startWidth + (e.clientX - startX)));
    setColWidths((prev) => ({ ...prev, [key]: newWidth }));
  }, []);

  const handleResizeEnd = useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback((key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { key, startX: e.clientX, startWidth: colWidths[key] };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [colWidths, handleResizeMove, handleResizeEnd]);

  useEffect(() => () => {
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd]);

  const tableWidth = Object.values(colWidths).reduce((a, b) => a + b, 0);

  return { colWidths, tableWidth, handleResizeStart };
}

export { ICON_COL_MIN_WIDTH };
export default useResizableColumns;
